import { ENFORCE_STRIPE } from "../lib/subscriptions";

import { User } from "@supabase/supabase-js";
import posthog from "posthog-js";
import { useState, useEffect, createContext, useContext, useMemo } from "react";
import { createClient } from "../utils/supabase/client";
import {
  Organization,
  organizationsGetPersonalOrganization,
  organizationsGetUserOrganizations,
} from "@wayak/ai/sdk";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys, LONG_STALE_TIME, GC_TIME } from "@wayak/ai";

type AuthContextType = {
  user: User | null;
  loading: boolean;
  subscribed: boolean;
  organizations: Organization[];
  selectedSpaceIdentifier: string;
  updateSubscribed: (status: boolean) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  enforceStripe: boolean;
  setSelectedSpaceIdentifier: (identifier: string) => void;
};

export const useAuth: () => AuthContextType = () => {
  const supabase = createClient();
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [subscribed, setSubscribed] = useState(false);
  const enforceStripe = ENFORCE_STRIPE;
  const [selectedSpaceIdentifier, setSelectedSpaceIdentifier] =
    useState<string>("");

  useEffect(() => {
    // Check active sessions and set one if exists
    async function getUser() {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (user) {
        setUser(user);
        posthog.identify(user?.id, { email: user?.email });
      }
      setAuthLoading(false);
    }

    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === "INITIAL_SESSION") {
          console.log("initial session");
        } else if (event === "SIGNED_IN") {
          console.log("signed in");
        } else if (event === "SIGNED_OUT") {
          console.log("signed out");
          posthog.reset();
        } else if (event === "PASSWORD_RECOVERY") {
          console.log("password recovery");
          window.location.href = "/auth/update-password";
        } else if (event === "TOKEN_REFRESHED") {
          console.log("token refreshed");
        } else if (event === "USER_UPDATED") {
          console.log("user updated");
        }
        setUser(session?.user ?? null);
      },
    );

    getUser();

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [supabase]);

  // Organizations fetched via React Query with 30-min staleTime
  const organizationsQuery = useQuery({
    queryKey: queryKeys.organizations.list(user?.id ?? ""),
    queryFn: async () => {
      const response = await organizationsGetUserOrganizations();
      const orgs = response.data;
      const organizations = Array.isArray(orgs) ? orgs : [];
      const hasPersonalOrganization = organizations.some(
        (org) =>
          org.kind === "personal" ||
          (!!user?.id && org.personal_owner_id === user.id),
      );

      if (hasPersonalOrganization) {
        return organizations;
      }

      const personalResponse = await organizationsGetPersonalOrganization();
      const personalOrganization = personalResponse.data;
      if (!personalOrganization?.id) {
        return organizations;
      }

      return [
        personalOrganization,
        ...organizations.filter((org) => org.id !== personalOrganization.id),
      ];
    },
    staleTime: LONG_STALE_TIME,
    gcTime: GC_TIME,
    enabled: !!user,
  });

  const organizations = organizationsQuery.data ?? [];
  const personalOrganization = useMemo(
    () =>
      organizations.find(
        (org) =>
          org.kind === "personal" ||
          (!!user?.id && org.personal_owner_id === user.id),
      ),
    [organizations, user?.id],
  );

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !user ||
      organizationsQuery.isPending
    ) {
      return;
    }

    const organizationIds = new Set(organizations.map((org) => org.id));
    const fallbackIdentifier = personalOrganization?.id ?? organizations[0]?.id;
    const saved = localStorage.getItem("selectedSpaceIdentifier");

    if (!fallbackIdentifier) {
      setSelectedSpaceIdentifier("");
      localStorage.removeItem("selectedSpaceIdentifier");
      return;
    }

    const nextIdentifier =
      saved && saved !== "personal" && organizationIds.has(saved)
        ? saved
        : fallbackIdentifier;

    setSelectedSpaceIdentifier(nextIdentifier);
    localStorage.setItem("selectedSpaceIdentifier", nextIdentifier);
  }, [
    organizations,
    organizationsQuery.isPending,
    personalOrganization?.id,
    user,
  ]);

  const loading = authLoading || (!!user && organizationsQuery.isPending);

  const signIn = async (email: string, password: string) => {
    setAuthLoading(true);
    const signInResult = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (signInResult.error) {
      throw signInResult.error;
    }
    setUser(signInResult.data?.user ?? null);
    setAuthLoading(false);
  };

  const signUp = async (email: string, password: string) => {
    const { error, data } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
    setUser(data?.user ?? null);
  };

  const resetPassword = async (email: string) => {
    console.log("resetting password");
    const origin = window.location.origin;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${origin}/auth/update-password`,
    });
    if (error) throw error;
  };

  const signOut = async () => {
    setAuthLoading(true);
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUser(null);
    queryClient.clear();
    setAuthLoading(false);
    window.location.href = "/";
  };

  const updateSubscribed = (status: boolean) => {
    console.log("updating subscribed status");
    setSubscribed(status);
  };

  const updateSelectedSpaceIdentifier = (identifier: string) => {
    setSelectedSpaceIdentifier(identifier);
    localStorage.setItem("selectedSpaceIdentifier", identifier);
  };

  return {
    user,
    organizations,
    selectedSpaceIdentifier,
    loading,
    subscribed,
    updateSubscribed,
    signIn,
    signUp,
    resetPassword,
    signOut,
    enforceStripe,
    setSelectedSpaceIdentifier: updateSelectedSpaceIdentifier,
  };
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  organizations: [],
  selectedSpaceIdentifier: "",
  setSelectedSpaceIdentifier: () => {},
  loading: true,
  subscribed: false,
  updateSubscribed: () => {},
  signIn: async () => {},
  signUp: async () => {},
  resetPassword: async () => {},
  signOut: async () => {},
  enforceStripe: false,
});

export const useAuthContext = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const auth = useAuth();

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}
